import type { MetricSourceState, ProspectPage, ScheduledJob, StatusResponse } from '../types';

export interface GrowthState {
  label: string;
  detail: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}

type Scheduler = Pick<StatusResponse, 'schedulerEnabled' | 'schedulerRunning'>;

export function schedulingState(status?: Scheduler): GrowthState {
  if (!status) return { label: 'Automatic scheduling is unconfirmed', detail: 'Current operating status is unavailable.', tone: 'warning' };
  if (!status.schedulerEnabled) return { label: 'Automatic scheduling is off', detail: 'Recurring work will not run while scheduling is disabled.', tone: 'neutral' };
  if (status.schedulerRunning === true) return { label: 'Automatic scheduling is running', detail: 'Scheduled work continues in the background. Reviews and delivery limits still apply.', tone: 'success' };
  if (status.schedulerRunning === false) return { label: 'Automatic scheduling is stopped', detail: 'Scheduling is enabled, but its worker is not running. Check scheduling and agent runs.', tone: 'danger' };
  return { label: 'Scheduler activity is unconfirmed', detail: 'Scheduling is enabled, but the running state was not reported.', tone: 'warning' };
}

const jobLabels: Record<string, string> = {
  'metrics-collection': 'Collect growth metrics', director: 'Director priorities', partner: 'Partner research report',
  content: 'Customer marketing article', 'content-partner': 'Partner article', 'content-venue': 'Venue story',
  activation: 'Activation review', board: 'Weekly growth review', 'customer-success': 'Customer success report',
  retrospective: 'Review recent work', 'approved-work': 'Process approved work', 'customer-mail': 'Check support inbox',
  'prospect-contact-refresh': 'Refresh business contacts', 'prospect-research': 'Find partner prospects',
  'prospect-contact-preview': 'Prepare contacted prospect preview',
  'direct-venue-research': 'Find venue prospects', 'prospect-outreach': 'Review and send outreach',
  'apollo-contact-review': 'Review saved Apollo contacts', 'apollo-auto-lookup': 'Find partner contacts with Apollo',
};

export function scheduledJobLabel(id: string): string {
  return jobLabels[id] || 'Scheduled work';
}

export function nextJobs(jobs: ScheduledJob[], now = Date.now(), limit = 3): ScheduledJob[] {
  return jobs.filter(job => job.enabled === true && job.running === true
    && job.nextRun !== null && Number.isFinite(Date.parse(job.nextRun)) && Date.parse(job.nextRun) >= now)
    .sort((a, b) => Date.parse(a.nextRun!) - Date.parse(b.nextRun!) || a.id.localeCompare(b.id))
    .slice(0, Math.max(0, limit));
}

export function knownCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

export function outreachState(page?: ProspectPage, status?: Scheduler, now = Date.now()): GrowthState {
  if (!page) return { label: 'Status unavailable', detail: 'Outreach settings and recent results could not be confirmed.', tone: 'warning' };
  const outreach = page.outreach;
  if (!outreach.enabled) return { label: 'Outreach is disabled', detail: 'Prospects can be researched while initial outreach is off.', tone: 'neutral' };
  if (outreach.paused) return { label: 'Outreach is paused', detail: 'Review the outreach controls before expecting another cycle.', tone: 'warning' };
  if (!outreach.sending_enabled) return { label: 'Sending is disabled', detail: 'Director review can run, but initial emails cannot send.', tone: 'neutral' };
  if (outreach.error_code) return { label: 'Last cycle needs checking', detail: outreach.error_code === 'prospect_outreach_review_failed'
    ? 'Some Director reviews failed. Other approved work and reply checks can continue.'
    : 'The last cycle reported a failure. Check outreach before relying on delivery.', tone: 'warning' };
  if (!status?.schedulerEnabled || status.schedulerRunning !== true) return {
    label: 'Waiting for confirmed scheduling', detail: 'Outreach is enabled; automatic execution is not currently confirmed.', tone: 'warning',
  };
  if (!outreach.last_cycle_at || !Number.isFinite(Date.parse(outreach.last_cycle_at))) return {
    label: 'Awaiting a recorded cycle', detail: 'Sending is enabled, but a completed cycle time is not available.', tone: 'neutral',
  };
  if (Date.parse(outreach.last_cycle_at) > now || now - Date.parse(outreach.last_cycle_at) > 90 * 60 * 1000) return {
    label: 'Cycle timing needs checking', detail: 'The last recorded cycle is more than 90 minutes old or has an unexpected future timestamp.', tone: 'warning',
  };
  return { label: 'Scheduled', detail: 'Eligible prospects pass independent Director review before sending.', tone: 'success' };
}

export function sourceState(sources?: MetricSourceState[], now = Date.now()): GrowthState {
  if (!sources?.length) return { label: 'Source freshness unavailable', detail: 'No source collection states were reported. Check the metric evidence before making decisions.', tone: 'warning' };
  const failed = sources.filter(source => source.status !== 'COMPLETE');
  if (failed.length) return { label: 'Source collection needs checking', detail: `${failed.length} of ${sources.length} sources are stale, failed or not yet collected.`, tone: 'warning' };
  if (sources.some(source => !source.collectedAt || !Number.isFinite(Date.parse(source.collectedAt)))) return {
    label: 'Collection dates unavailable', detail: 'Collection completed, but one or more source timestamps are missing.', tone: 'warning',
  };
  if (sources.some(source => Date.parse(source.collectedAt!) > now)) return {
    label: 'Collection dates need checking', detail: 'One or more source collection timestamps are in the future.', tone: 'warning',
  };
  const old = sources.filter(source => now - Date.parse(source.collectedAt!) > 36 * 60 * 60 * 1000);
  if (old.length) return { label: 'Collection dates need checking', detail: `${old.length} of ${sources.length} sources were last collected more than 36 hours ago.`, tone: 'warning' };
  return { label: 'Recent collections available', detail: 'Collection completed within the last 36 hours. Each metric retains its own population and observation window.', tone: 'neutral' };
}
